import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';

import { useAuth } from '../../lib/AuthContext';
import { uiLogger } from '../../utils/loggerInstance';
import { useOptionalNavigation } from '../../lib/NavigationContext';
import onboardingService from '../../services/onboardingService';
import { shouldSuppressRedirect } from '../../utils/navigationRedirectHelpers';
import { handleAuthError, isUnauthorizedError } from '../../services/authErrorHelper';
import { LoadingSpinnerFullscreen } from '../ui/LoadingSpinner';
import { AccountLoadErrorCard } from './AccountLoadErrorCard';
import { TERMS_ACCEPTED_STORAGE_KEY } from '../../config/constants';
import { LOCAL_STORAGE_KEYS, isSafeRelativeRedirect } from '../../constants/localStorageKeys';
import { ORGANIZATION_KEYS } from '../../constants/queryKeys';
import { userService } from '../../services/userService';
import { organizationService } from '../../services/organizationService';
import {
  billingProfileManagementPath,
  fetchOrganizationForBillingGate,
  isPaymentFlowRoute,
  isSubscriptionOptionalRoute,
  normalizeBillingProfilePathname,
  PROFILE_BILLING_PATH,
  PROFILE_ENTERPRISE_BILLING_PATH,
  probeActiveSubscription,
  pricingPathForOrganization,
  recordBillingEntitlementMetric,
} from '../../services/billingEntitlementPolicy';
import { useOrganization } from '../../hooks/useOrganization';
import { useBillingEnforcementPolicy } from '../../hooks/useBillingEnforcementPolicy';
import { CONTACT_INFO } from '../../constants/contact';
import { BillingEnforcementBanner } from '../common/BillingEnforcementBanner';
import { BillingUrgentNudgeDialog } from '../common/BillingUrgentNudgeDialog';
import { isTransientBillingPolicyFetchError } from '../../utils/billingPolicyFetchErrors';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAuth?: boolean;
  skipOnboardingCheck?: boolean;
}

type NavigateTo = (path: string, options?: { replace?: boolean }) => void;

function failClosedToBilling(
  navigateTo: NavigateTo,
  currentPath: string,
  organization: Awaited<ReturnType<typeof organizationService.getOrganization>> | null,
  allowStayWithoutRedirect: boolean,
  reason: string,
  error: unknown,
): void {
  uiLogger.warn(`ProtectedRoute: ${reason}`, { error, currentPath });
  if (allowStayWithoutRedirect) return;
  navigateTo(pricingPathForOrganization(organization), { replace: true });
}

/** False = subscription API failed transiently — caller may allow session and show recovery UI. */
async function performOnboardingCheck(
  queryClient: QueryClient,
  userId: string,
  currentPath: string,
  navigateTo: NavigateTo,
  _navigateToLogin: () => void,
): Promise<boolean> {
  let organization: Awaited<ReturnType<typeof organizationService.getOrganization>> | null = null;
  /** Narrow exempt list: org-intake redirect must still run from most profile pages. */
  const isBillingPath =
    currentPath === '/checkout' ||
    currentPath === '/return' ||
    currentPath === '/profile/pricing' ||
    currentPath === PROFILE_ENTERPRISE_BILLING_PATH ||
    currentPath === PROFILE_BILLING_PATH;
  const allowWithoutActiveSubscription = isSubscriptionOptionalRoute(currentPath);
  const isOrgIntakePath =
    currentPath === '/onboarding' ||
    currentPath === '/org-setup-pending' ||
    currentPath === '/user-onboarding';

  // Billing gate has priority over onboarding.
  // If user has no subscription, keep them on billing-required pages and
  // redirect other routes to the right pricing screen.
  try {
    organization = await fetchOrganizationForBillingGate(() =>
      queryClient.ensureQueryData({
        queryKey: ORGANIZATION_KEYS.all,
        queryFn: organizationService.getOrganization,
      }),
    );
  } catch (error) {
    recordBillingEntitlementMetric({
      surface: 'protected_route',
      organizationOutcome: 'fetch_exhausted',
      error,
    });
    // Do not redirect to pricing when org resolution fails; callers should show explicit recovery UI.
    throw error;
  }

  if (organization && !organization.intake_completed_at && !isBillingPath && !isOrgIntakePath) {
    if (organization.role === 'owner' || organization.role === 'admin') {
      navigateTo('/onboarding', { replace: true });
    } else {
      navigateTo('/org-setup-pending', { replace: true });
    }
    return true;
  }

  const subscription = await probeActiveSubscription(organization);
  recordBillingEntitlementMetric({
    surface: 'protected_route',
    organizationOutcome: 'success',
    organization,
    subscription,
  });

  if (subscription.status === 'transient') {
    uiLogger.warn(
      'ProtectedRoute: Subscription verification failed (transient); not redirecting to billing',
      { error: subscription.error, currentPath },
    );
    return false;
  }

  if (subscription.status === 'fail_closed') {
    failClosedToBilling(
      navigateTo,
      currentPath,
      organization,
      allowWithoutActiveSubscription,
      'Subscription verification failed; fail-closed to billing',
      subscription.error,
    );
    return true;
  }

  if (subscription.status === 'inactive') {
    if (allowWithoutActiveSubscription) return true;
    navigateTo(pricingPathForOrganization(organization), { replace: true });
    return true;
  }

  uiLogger.debug('ProtectedRoute: Checking if user has completed onboarding', { userId });
  const hasCompletedOnboarding = await onboardingService.hasUserCompletedOnboarding(userId);
  if (hasCompletedOnboarding) return true;

  if (isPaymentFlowRoute(currentPath)) {
    return true;
  }

  let termsAccepted = false;
  try {
    const data = await userService.getTermsAcceptance();
    termsAccepted = data?.terms_accepted === true;
    if (termsAccepted && typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem(TERMS_ACCEPTED_STORAGE_KEY, 'true');
    }
  } catch (err) {
    uiLogger.warn(
      'ProtectedRoute: Failed to fetch terms acceptance, treating as inconclusive (not redirecting)',
      { error: err },
    );
    throw err;
  }

  if (!termsAccepted) {
    uiLogger.info(
      'ProtectedRoute: User needs to accept terms before onboarding, redirecting to accept-terms',
      { userId },
    );
    navigateTo('/accept-terms', { replace: true });
  } else {
    const isB2BOrgUser = !!organization && !!organization.intake_completed_at;
    uiLogger.info(
      'ProtectedRoute: User needs to complete onboarding, redirecting to first onboarding step',
      { userId, isB2BOrgUser },
    );
    navigateTo('/user-onboarding', { replace: true });
  }
  return true;
}

function handleOnboardingCheckError(
  error: unknown,
  currentPath: string,
  navigateToLogin: () => void,
): void {
  uiLogger.error('ProtectedRoute: Error checking onboarding status:', { error });
  const isHandled = handleAuthError({
    error,
    context: 'onboarding check',
    onUnauthorized: () => navigateToLogin(),
    onForbidden: () => {},
  });
  if (isHandled) return;

  const responseError = error && typeof error === 'object' && 'response' in error ? error : null;
  const status = responseError
    ? (responseError as { response?: { status?: number } }).response?.status
    : undefined;
  if (status != null && status >= 500) {
    uiLogger.warn('ProtectedRoute: Server error during onboarding check - not redirecting', {
      status,
      currentPath,
    });
    return;
  }
  uiLogger.warn('ProtectedRoute: Unknown error during onboarding check - not redirecting');
}

const ONBOARDING_ROUTES = [
  '/accept-terms',
  '/user-onboarding',
  '/goal-selection',
  '/industry-selection',
  '/workspace-selection',
];
const B2B_ONLY_ONBOARDING_ROUTES = ['/onboarding', '/org-setup-pending'];
const LEGACY_ONBOARDING_ROUTES = ['/goal-selection', '/industry-selection', '/workspace-selection'];

/**
 * Onboarding / org-intake routes that share the onboarding shell. Used to delay rendering the
 * main app until the shell is ready (avoids layout flash).
 * Membership: primary onboarding steps, B2B-only intake routes, and legacy selection routes (deduped by Set).
 */
const ONBOARDING_SHELL_GATE_PATHS = new Set<string>([
  ...ONBOARDING_ROUTES,
  ...B2B_ONLY_ONBOARDING_ROUTES,
  ...LEGACY_ONBOARDING_ROUTES,
]);

/** Suspended users may only use these **exact** client routes (avoid `startsWith('/profile/billing')` → matches `/profile/billing-…`). */
const BILLING_SUSPENSION_ALLOWED_EXACT_PATHS = new Set<string>([
  PROFILE_BILLING_PATH,
  PROFILE_ENTERPRISE_BILLING_PATH,
  '/profile/pricing',
  '/checkout',
  '/return',
]);

/**
 * Additional allowed URL prefixes when the product has nested segments. Each entry **must**
 * end with `/` so sibling paths (e.g. `/profile/pricing-other`) are not admitted.
 */
const BILLING_SUSPENSION_ALLOWED_PATH_PREFIXES: readonly string[] = [
  '/checkout/',
  '/profile/pricing/',
];

function isBillingSuspensionAllowedPath(pathname: string): boolean {
  const p = normalizeBillingProfilePathname(pathname);
  if (BILLING_SUSPENSION_ALLOWED_EXACT_PATHS.has(p)) return true;
  return BILLING_SUSPENSION_ALLOWED_PATH_PREFIXES.some((prefix) => p.startsWith(prefix));
}

function shouldRunOnboardingCheck(
  currentPath: string,
  flags: {
    isAuthenticated: boolean;
    hasUser: boolean;
    loading: boolean;
    authReady: boolean;
    skipOnboardingCheck: boolean;
    requireAuth: boolean;
    hasCachedCompletedOnboarding: boolean;
  },
): boolean {
  if (shouldSuppressRedirect(currentPath)) return false;
  if (!flags.isAuthenticated || !flags.hasUser || flags.loading || !flags.authReady) return false;
  if (flags.skipOnboardingCheck || !flags.requireAuth) return false;
  if (flags.hasCachedCompletedOnboarding) return false;
  if (currentPath === '/onboarding' || currentPath === '/org-setup-pending') return false;
  if (ONBOARDING_ROUTES.includes(currentPath)) return false;
  return true;
}

/**
 * Route wrapper that handles authentication requirements
 *
 * @param children - The components to render if conditions are met
 * @param requireAuth - If true, user must be authenticated to access the route (default: true)
 * @param skipOnboardingCheck - If true, don't redirect based on onboarding status (default: false)
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requireAuth = true,
  skipOnboardingCheck = false,
}) => {
  const { isAuthenticated, loading, session, user, authReady } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const navigation = useOptionalNavigation();
  const fallbackNavigateToLogin = useCallback(
    () => navigate('/login', { replace: true }),
    [navigate],
  );
  const fallbackNavigateToChat = useCallback(
    () => navigate('/chat', { replace: true }),
    [navigate],
  );
  const fallbackNavigateTo = useCallback(
    (path: string, options?: { replace?: boolean }) => navigate(path, options),
    [navigate],
  );
  const navigateToLogin = navigation?.navigateToLogin ?? fallbackNavigateToLogin;
  const navigateToChat = navigation?.navigateToChat ?? fallbackNavigateToChat;
  const navigateTo = navigation?.navigateTo ?? fallbackNavigateTo;
  const queryClient = useQueryClient();
  const shouldFetchOrg = requireAuth && isAuthenticated && !loading && authReady && !!user?.id;
  const {
    data: organization,
    isLoading: orgLoading,
    isError: isOrgQueryError,
    error: orgFetchError,
    refetch: refetchOrganization,
  } = useOrganization(shouldFetchOrg);
  /** Org-scoped billing enforcement API; skip for B2C (no org) to avoid GET /billing-enforcement/me → 404 and extra loading gate. */
  const shouldFetchBillingEnforcement = shouldFetchOrg && !orgLoading && organization != null;
  const {
    data: billingPolicy,
    isLoading: billingPolicyLoading,
    isError: billingPolicyError,
    error: billingPolicyFetchError,
    refetch: refetchBillingPolicy,
  } = useBillingEnforcementPolicy(shouldFetchBillingEnforcement, organization?.id);
  const orgBillingPolicy = shouldFetchBillingEnforcement ? billingPolicy : undefined;
  const [dismissedUrgentNudgeKey, setDismissedUrgentNudgeKey] = useState<string | null>(null);
  const urgentNudgeKey = React.useMemo(() => {
    if (!orgBillingPolicy || orgBillingPolicy.state === 'active') {
      return null;
    }
    return `${orgBillingPolicy.state}-${String(orgBillingPolicy.delinquent_days ?? 0)}-${String(orgBillingPolicy.nudge_level ?? 'none')}`;
  }, [orgBillingPolicy]);
  const showUrgentNudge =
    requireAuth &&
    isAuthenticated &&
    !!orgBillingPolicy &&
    !!orgBillingPolicy.nudge_urgent_modal &&
    orgBillingPolicy.state !== 'active' &&
    urgentNudgeKey != null &&
    dismissedUrgentNudgeKey !== urgentNudgeKey;
  const [isCheckingOnboarding, setIsCheckingOnboarding] = useState(false);
  const [entitlementCheckTransientFailure, setEntitlementCheckTransientFailure] = useState(false);
  const [entitlementCheckRetryKey, setEntitlementCheckRetryKey] = useState(0);
  const [onboardingShellReady, setOnboardingShellReady] = useState(
    () => !ONBOARDING_SHELL_GATE_PATHS.has(location.pathname),
  );
  const redirectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!shouldFetchOrg || !isOrgQueryError || orgFetchError == null) return;
    handleAuthError({
      error: orgFetchError,
      context: 'organization fetch',
      onUnauthorized: () => navigateToLogin(),
      onForbidden: () => {},
    });
  }, [shouldFetchOrg, isOrgQueryError, orgFetchError, navigateToLogin]);

  useEffect(() => {
    if (!shouldFetchBillingEnforcement || !billingPolicyError || billingPolicyFetchError == null)
      return;
    uiLogger.warn('ProtectedRoute: Failed to load billing enforcement policy', {
      error: billingPolicyFetchError,
      currentPath: location.pathname,
    });
  }, [
    shouldFetchBillingEnforcement,
    billingPolicyError,
    billingPolicyFetchError,
    location.pathname,
  ]);

  useEffect(() => {
    if (
      !requireAuth ||
      !isAuthenticated ||
      loading ||
      !authReady ||
      shouldSuppressRedirect(location.pathname) ||
      (shouldFetchOrg && orgLoading) ||
      (shouldFetchBillingEnforcement && billingPolicyLoading)
    ) {
      return;
    }
    if (orgBillingPolicy?.state !== 'suspended') {
      return;
    }
    if (isBillingSuspensionAllowedPath(location.pathname)) {
      return;
    }
    const billingFallback = billingProfileManagementPath(!!organization);
    navigateTo(billingFallback, { replace: true });
  }, [
    requireAuth,
    isAuthenticated,
    loading,
    authReady,
    location.pathname,
    orgBillingPolicy?.state,
    navigateTo,
    organization,
    orgLoading,
    shouldFetchOrg,
    shouldFetchBillingEnforcement,
    billingPolicyLoading,
  ]);

  // Onboarding shell: resolve redirects + completion checks before painting (no step-on-then-redirect flash).
  useEffect(() => {
    const currentPath = location.pathname;
    if (shouldSuppressRedirect(currentPath)) {
      setOnboardingShellReady(true);
      return;
    }

    const inShellGate = requireAuth && ONBOARDING_SHELL_GATE_PATHS.has(currentPath);

    if (!inShellGate) {
      setOnboardingShellReady(true);
      return;
    }

    if (!requireAuth || !isAuthenticated || loading || !authReady || !user?.id) {
      if (inShellGate) setOnboardingShellReady(false);
      return;
    }

    if (orgLoading) {
      setOnboardingShellReady(false);
      return;
    }

    if (isOrgQueryError) {
      uiLogger.warn('ProtectedRoute: Skipping onboarding shell gate (org fetch failed)', {
        error: orgFetchError,
        currentPath,
      });
      setOnboardingShellReady(true);
      return;
    }

    setOnboardingShellReady(false);

    let cancelled = false;
    const finish = (didNavigate: boolean) => {
      if (cancelled || didNavigate) return;
      setOnboardingShellReady(true);
    };

    const run = async () => {
      let didNavigate = false;
      const nav = (path: string) => {
        didNavigate = true;
        navigateTo(path, { replace: true });
      };

      const isB2BUser = !!organization;
      const isB2BOnlyRoute = B2B_ONLY_ONBOARDING_ROUTES.includes(currentPath);
      const isLegacyOnboardingRoute = LEGACY_ONBOARDING_ROUTES.includes(currentPath);
      const isUserOnboardingPath = currentPath === '/user-onboarding';
      const isOrgOnboardingPath =
        currentPath === '/onboarding' || currentPath === '/org-setup-pending';

      if (isLegacyOnboardingRoute) {
        nav('/user-onboarding');
        return;
      }

      if (!isB2BUser && isB2BOnlyRoute) {
        nav('/user-onboarding');
        return;
      }

      if (isB2BUser && organization) {
        const isAdminOrOwner = organization.role === 'owner' || organization.role === 'admin';
        const intakeCompleted = !!organization.intake_completed_at;

        if (!intakeCompleted && isAdminOrOwner && currentPath !== '/onboarding') {
          nav('/onboarding');
          return;
        }
        if (!intakeCompleted && !isAdminOrOwner && currentPath !== '/org-setup-pending') {
          nav('/org-setup-pending');
          return;
        }
      }

      if (!isB2BUser && isOrgOnboardingPath) {
        nav('/user-onboarding');
        return;
      }

      if (!organization) {
        if (isUserOnboardingPath) {
          try {
            const hasCompleted = await onboardingService.hasUserCompletedOnboarding(user.id);
            if (cancelled) return;
            if (hasCompleted) {
              nav('/chat');
              return;
            }
          } catch (error) {
            uiLogger.warn('ProtectedRoute: Failed onboarding guard on user-onboarding path', {
              error,
            });
          }
        }
        finish(didNavigate);
        return;
      }

      const org = organization;
      const isAdminOrOwner = org.role === 'owner' || org.role === 'admin';
      const intakeCompleted = !!org.intake_completed_at;

      if (!intakeCompleted) {
        if (isAdminOrOwner && currentPath !== '/onboarding') {
          nav('/onboarding');
          return;
        }
        if (!isAdminOrOwner && currentPath !== '/org-setup-pending') {
          nav('/org-setup-pending');
        }
        finish(didNavigate);
        return;
      }

      if (isOrgOnboardingPath) {
        nav('/user-onboarding');
        return;
      }

      if (isUserOnboardingPath) {
        try {
          const hasCompleted = await onboardingService.hasUserCompletedOnboarding(user.id);
          if (cancelled) return;
          if (hasCompleted) {
            nav('/chat');
            return;
          }
        } catch (error) {
          uiLogger.warn('ProtectedRoute: Failed onboarding guard on user-onboarding path', {
            error,
          });
        }
      }

      finish(didNavigate);
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [
    requireAuth,
    isAuthenticated,
    loading,
    authReady,
    user?.id,
    location.pathname,
    navigateTo,
    organization,
    orgLoading,
    isOrgQueryError,
    orgFetchError,
  ]);

  // Check session expiration
  useEffect(() => {
    // Don't redirect from email verification page or during signup
    const currentPath = location.pathname;
    if (shouldSuppressRedirect(currentPath)) {
      // Allow user to stay on email verification page even if session expires
      // They need to complete email verification first
      return;
    }

    if (session?.expires_at) {
      const expiresAt = new Date(session.expires_at * 1000);
      const now = new Date();

      if (expiresAt <= now) {
        uiLogger.info('Session expired, redirecting to login');
        navigateToLogin();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, location.pathname]);

  // Check onboarding / billing entitlement for authenticated users (no debounce — stale runs are ignored via `cancelled`).
  useEffect(() => {
    const currentPath = location.pathname;
    const subscriptionOptionalPath = isSubscriptionOptionalRoute(currentPath);
    const hasCompletedOnboardingCache = onboardingService.getCachedCompletedOnboarding(user?.id);
    const runCheck = shouldRunOnboardingCheck(currentPath, {
      isAuthenticated,
      hasUser: !!user,
      loading,
      authReady,
      skipOnboardingCheck,
      requireAuth,
      hasCachedCompletedOnboarding: hasCompletedOnboardingCache,
    });

    if (!runCheck) {
      setEntitlementCheckTransientFailure(false);
      return () => {};
    }

    let cancelled = false;

    const runOnboardingCheck = async () => {
      setEntitlementCheckTransientFailure(false);
      if (!subscriptionOptionalPath) {
        setIsCheckingOnboarding(true);
      }
      try {
        const completed = await performOnboardingCheck(
          queryClient,
          user!.id,
          currentPath,
          navigateTo,
          navigateToLogin,
        );
        if (cancelled) return;
        if (!completed) {
          setEntitlementCheckTransientFailure(true);
        }
      } catch (error) {
        if (!cancelled) {
          handleOnboardingCheckError(error, currentPath, navigateToLogin);
        }
      } finally {
        if (!cancelled) {
          setIsCheckingOnboarding(false);
        }
      }
    };

    void runOnboardingCheck();

    return () => {
      cancelled = true;
      setIsCheckingOnboarding(false);
    };
  }, [
    isAuthenticated,
    user,
    loading,
    authReady,
    skipOnboardingCheck,
    requireAuth,
    location.pathname,
    navigateTo,
    navigateToLogin,
    queryClient,
    entitlementCheckRetryKey,
  ]);

  // Handle authentication redirects
  useEffect(() => {
    // Authentication required but user not authenticated
    if (requireAuth && !isAuthenticated && !loading) {
      // Store the current path for redirect after login
      const currentPath = location.pathname + location.search;
      if (currentPath !== '/login' && currentPath !== '/' && isSafeRelativeRedirect(currentPath)) {
        localStorage.setItem(LOCAL_STORAGE_KEYS.REDIRECT_AFTER_LOGIN, currentPath);
        uiLogger.debug(`Storing redirect path for after login`, { path: currentPath });
      }

      uiLogger.warn(
        'ProtectedRoute: Authentication required but user not authenticated, redirecting to login',
        {
          requireAuth,
          isAuthenticated,
          loading,
          authReady,
          currentPath: location.pathname,
          user: !!user,
          session: !!session,
        },
      );
      navigateToLogin();
      return;
    }

    // Public route + authenticated: redirect to chat only for publicRoutesToRedirectFrom (/, /login, /signup).
    // Other requireAuth={false} paths stay reachable (e.g. /verify-email, /reset-password, /email-marketing-preferences).
    // Don't redirect from /signup if signup is in progress (user is being redirected to /verify-email)
    // Don't redirect if logout is in progress (user clicked "back to login")
    if (!requireAuth && isAuthenticated && !loading && authReady) {
      const publicRoutesToRedirectFrom = ['/login', '/signup', '/'];
      const isSignupInProgress = sessionStorage.getItem('signup_in_progress') === 'true';
      const isLoggingOut = sessionStorage.getItem('logging_out') === 'true';

      // Don't redirect if logout is in progress or signup is in progress
      if (
        publicRoutesToRedirectFrom.includes(location.pathname) &&
        !isSignupInProgress &&
        !isLoggingOut
      ) {
        // Clear any existing timeout
        if (redirectTimeoutRef.current) {
          clearTimeout(redirectTimeoutRef.current);
          redirectTimeoutRef.current = null;
        }

        // For /login, add a small delay to allow logout to complete if it was just triggered
        if (location.pathname === '/login') {
          redirectTimeoutRef.current = setTimeout(() => {
            // Check if logout completed (user is no longer authenticated)
            // Also check if logging_out flag is still set
            const stillAuthenticated = !loading && authReady && isAuthenticated;
            const stillLoggingOut = sessionStorage.getItem('logging_out') === 'true';

            if (stillAuthenticated && !stillLoggingOut) {
              navigateToChat();
            }
            redirectTimeoutRef.current = null;
          }, 1500);
        } else {
          // For other routes, redirect immediately
          navigateToChat();
        }
      }
    }

    // Cleanup timeout on unmount or when dependencies change
    return () => {
      if (redirectTimeoutRef.current) {
        clearTimeout(redirectTimeoutRef.current);
        redirectTimeoutRef.current = null;
      }
    };
  }, [
    requireAuth,
    isAuthenticated,
    loading,
    authReady,
    location.pathname,
    navigateToLogin,
    navigateToChat,
  ]);

  const isSuspendedState = orgBillingPolicy?.state === 'suspended';
  const isSuspendedRouteAllowed = isBillingSuspensionAllowedPath(location.pathname);
  const shouldBlockSuspendedRoute =
    requireAuth &&
    isAuthenticated &&
    isSuspendedState &&
    !shouldSuppressRedirect(location.pathname) &&
    !isSuspendedRouteAllowed;

  // Loading states
  if (isCheckingOnboarding) {
    return <LoadingSpinnerFullscreen size='xl' text='Loading your account...' />;
  }

  if (loading) {
    return <LoadingSpinnerFullscreen size='xl' text='Loading your account...' />;
  }

  // Show loading while redirecting for authentication
  if (requireAuth && !isAuthenticated) {
    return <LoadingSpinnerFullscreen size='xl' text='Redirecting to login...' />;
  }

  // Consistent pre-gate for protected routes: resolve org state before rendering children.
  if (shouldFetchOrg && orgLoading) {
    return <LoadingSpinnerFullscreen size='xl' text='Loading your account...' />;
  }

  if (shouldFetchBillingEnforcement && billingPolicyLoading) {
    return <LoadingSpinnerFullscreen size='xl' text='Loading your account...' />;
  }

  // Org query failed after auth — before billing gate (org is the more specific failure).
  if (shouldFetchOrg && !orgLoading && isOrgQueryError) {
    if (isUnauthorizedError(orgFetchError)) {
      return <LoadingSpinnerFullscreen size='xl' text='Redirecting to login...' />;
    }
    return (
      <AccountLoadErrorCard
        onRetry={async () => {
          await refetchOrganization();
        }}
        fullScreen
      />
    );
  }

  // Billing policy failed after retries — fail closed (no full shell) so suspended UX cannot be bypassed.
  if (shouldFetchBillingEnforcement && !billingPolicyLoading && billingPolicyError) {
    if (isUnauthorizedError(billingPolicyFetchError)) {
      return <LoadingSpinnerFullscreen size='xl' text='Redirecting to login...' />;
    }
    const likelyTransient = isTransientBillingPolicyFetchError(billingPolicyFetchError);
    return (
      <AccountLoadErrorCard
        fullScreen
        title={
          likelyTransient
            ? "Can't verify billing status right now"
            : "We couldn't load your billing status"
        }
        description={
          likelyTransient ? (
            <>
              <p className='mb-3'>
                This is usually a temporary network or service issue. We retry automatically in the
                background — use Try again or wait a few seconds.
              </p>
              <p className='text-xs text-muted-foreground'>
                If this keeps failing after several attempts, contact{' '}
                <a
                  className='underline font-medium text-foreground'
                  href={`mailto:${CONTACT_INFO.supportEmail}`}
                >
                  {CONTACT_INFO.supportEmail}
                </a>
                .
              </p>
            </>
          ) : (
            <>
              We couldn&apos;t confirm your organization&apos;s billing enforcement state. Try
              again. If the problem continues, contact{' '}
              <a
                className='underline font-medium text-foreground'
                href={`mailto:${CONTACT_INFO.supportEmail}`}
              >
                {CONTACT_INFO.supportEmail}
              </a>
              .
            </>
          )
        }
        onRetry={() => refetchBillingPolicy()}
      />
    );
  }

  if (shouldBlockSuspendedRoute) {
    return <LoadingSpinnerFullscreen size='xl' text='Redirecting to billing...' />;
  }

  if (
    requireAuth &&
    isAuthenticated &&
    ONBOARDING_SHELL_GATE_PATHS.has(location.pathname) &&
    (!authReady || !onboardingShellReady)
  ) {
    return <LoadingSpinnerFullscreen size='xl' text='Loading your account...' />;
  }

  // Show loading while redirecting authenticated users from specific public routes
  // Don't redirect from /verify-email - allow users to verify their email even if authenticated
  // Also don't redirect if user just came from email verification (signup_in_progress was cleared)
  if (!requireAuth && isAuthenticated) {
    const publicRoutesToRedirectFrom = ['/login', '/signup', '/'];
    const isSignupInProgress = sessionStorage.getItem('signup_in_progress') === 'true';

    // Don't redirect if signup is still in progress or if we're on verify-email
    if (
      isSignupInProgress ||
      location.pathname === '/verify-email' ||
      location.pathname === '/reset-password'
    ) {
      return <>{children}</>;
    }

    if (publicRoutesToRedirectFrom.includes(location.pathname)) {
      // Add a small delay to allow logout to complete if user just clicked "back to login"
      // Check again after a brief moment to see if user is still authenticated
      return <LoadingSpinnerFullscreen size='xl' text='Redirecting to chat...' />;
    }
  }

  // Transient subscription/entitlement API outage — block the shell until retry succeeds (same pattern as org load failure).
  if (entitlementCheckTransientFailure && requireAuth && isAuthenticated) {
    return (
      <AccountLoadErrorCard
        fullScreen
        title="We couldn't confirm your subscription"
        description={
          <>
            This is usually temporary (network or payment service). Try again shortly. If you
            already pay for a plan and this keeps happening, contact{' '}
            <a
              className='underline font-medium text-foreground'
              href={`mailto:${CONTACT_INFO.supportEmail}`}
            >
              {CONTACT_INFO.supportEmail}
            </a>
            .
          </>
        }
        onRetry={() => setEntitlementCheckRetryKey((k) => k + 1)}
      />
    );
  }

  return (
    <>
      {requireAuth && isAuthenticated && orgBillingPolicy && showUrgentNudge && urgentNudgeKey ? (
        <BillingUrgentNudgeDialog
          policy={orgBillingPolicy}
          isOrgUser={!!organization}
          open
          onDismiss={() => setDismissedUrgentNudgeKey(urgentNudgeKey)}
        />
      ) : null}
      {requireAuth && isAuthenticated && orgBillingPolicy ? (
        <BillingEnforcementBanner policy={orgBillingPolicy} isOrgUser={!!organization} />
      ) : null}
      {children}
    </>
  );
};
